import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export interface Interest {
  id: string;
  name: string;
  category: string | null;
}

export const useInterests = () => {
  const [interests, setInterests] = useState<Interest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInterests = async () => {
      const { data, error } = await supabase
        .from("interests")
        .select("*")
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (!error && data) {
        setInterests(data);
      }
      setLoading(false);
    };

    fetchInterests();
  }, []);

  // Group interests by category
  const interestsByCategory = interests.reduce((acc, interest) => {
    const category = interest.category || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(interest);
    return acc;
  }, {} as Record<string, Interest[]>);

  return { interests, interestsByCategory, loading };
};

export const useUserInterests = () => {
  const { user } = useAuthContext();
  const [userInterestIds, setUserInterestIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const fetchUserInterests = useCallback(async () => {
    if (!user) {
      setUserInterestIds(new Set());
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("user_interests")
      .select("interest_id")
      .eq("user_id", user.id);

    if (!error && data) {
      setUserInterestIds(new Set(data.map((ui) => ui.interest_id)));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchUserInterests();
  }, [fetchUserInterests]);

  const addInterest = useCallback(async (interestId: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("user_interests")
      .insert({ user_id: user.id, interest_id: interestId });

    if (!error) {
      setUserInterestIds((prev) => new Set(prev).add(interestId));
    }
    return { error };
  }, [user]);

  const removeInterest = useCallback(async (interestId: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("user_interests")
      .delete()
      .eq("user_id", user.id)
      .eq("interest_id", interestId);

    if (!error) {
      setUserInterestIds((prev) => {
        const next = new Set(prev);
        next.delete(interestId);
        return next;
      });
    }
    return { error };
  }, [user]);

  const toggleInterest = useCallback(async (interestId: string) => {
    if (userInterestIds.has(interestId)) {
      return removeInterest(interestId);
    } else {
      return addInterest(interestId);
    }
  }, [userInterestIds, addInterest, removeInterest]);

  return {
    userInterestIds,
    loading,
    addInterest,
    removeInterest,
    toggleInterest,
    refetch: fetchUserInterests,
  };
};

// Get another user's interests for compatibility calculation
export const useProfileInterests = (profileId: string | undefined) => {
  const [interestIds, setInterestIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInterests = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_interests")
        .select("interest_id")
        .eq("user_id", profileId);

      if (!error && data) {
        setInterestIds(new Set(data.map((ui) => ui.interest_id)));
      }
      setLoading(false);
    };

    fetchInterests();
  }, [profileId]);

  return { interestIds, loading };
};

// Calculate compatibility score between two users based on shared interests
export const calculateCompatibility = (
  userInterests: Set<string>,
  profileInterests: Set<string>
): { score: number; shared: number; total: number } => {
  if (userInterests.size === 0 || profileInterests.size === 0) {
    return { score: 0, shared: 0, total: 0 };
  }

  let shared = 0;
  userInterests.forEach((id) => {
    if (profileInterests.has(id)) {
      shared++;
    }
  });

  // Jaccard similarity coefficient
  const union = new Set([...userInterests, ...profileInterests]);
  const score = Math.round((shared / union.size) * 100);

  return { score, shared, total: union.size };
};
